import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  imageWithUploadSchema,
  nameSchema,
  type ImageWithUpload,
  type OrganizationProfile,
} from '@hero/schema';
import { useOrgProfileUpdateApi } from '@hero/api-hooks';
import { CheckIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button';
import { Spinner } from '../../../app/Spinner';
import { DisplayNameField } from '../../../app/OrgProfileFields/DisplayNameField';
import { LogoField } from '../../../app/OrgProfileFields/LogoField';
import { makeChipFor, type OrgProfileSectionContext } from './types';

/**
 * Local RHF resolver for the General tab. Mirrors the top-level
 * shape of `orgProfileUpdateSchema` for the keys this form owns:
 * `{ displayName?, logo? }`. We keep both fields optional in the
 * resolver so the user can hit Save with only one of them edited.
 *
 * The server still enforces the "empty string never overwrites"
 * rule via `nameSchema` (`trim().min(1)`) so a blank `displayName`
 * gets rejected before reaching the DB; we mirror the rule on the
 * client so the user gets immediate feedback rather than waiting
 * for a round-trip.
 */
const generalSchema = z.object({
  displayName: nameSchema.optional(),
  logo: imageWithUploadSchema.optional(),
});

type GeneralFormValues = z.infer<typeof generalSchema>;

export interface GeneralProfileFormProps {
  /** Current profile snapshot used to hydrate initial values. */
  profile: Partial<OrganizationProfile> | undefined;
  /**
   * Where the form is rendered. The General section has no hard
   * financing requirement (logo / display name don't gate
   * underwriting), so today this only affects copy hints, not
   * gating. Kept on the prop surface for symmetry with the other
   * section forms.
   */
  displayFor?: OrgProfileSectionContext;
  /** Hide the section's Save button — useful when an outer
   *  drawer / wizard owns the action. */
  hideSubmit?: boolean;
  /** Disable every field (read-only view). */
  disabled?: boolean;
  /** Called after a successful save with the section patch sent. */
  onSaved?: (patch: { displayName?: string; logo?: ImageWithUpload }) => void;
}

/**
 * `GeneralProfileForm` — the General tab of the carrier-dashboard
 * profile. Edits two fields and saves an
 * `{ displayName?, logo? }` slice via the section-keyed
 * `useOrgProfileUpdateApi`.
 *
 * Why a separate tab for two fields? The display name + logo are
 * what every other party sees about the org *first* (cards,
 * agreements, side nav), so getting them in front of the user
 * isolated from legal/banking concerns is intentional. They also
 * have no financing-side requirement, so it's a "always-safe to
 * edit" tab.
 */
export function GeneralProfileForm({
  profile,
  displayFor = 'profile',
  hideSubmit,
  disabled,
  onSaved,
}: GeneralProfileFormProps) {
  const { t } = useTranslation('m-organization');
  const update = useOrgProfileUpdateApi();
  const chip = makeChipFor(displayFor);

  const defaultValues = useMemo<GeneralFormValues>(
    () => ({
      displayName: profile?.displayName,
      logo: pickLogo(profile),
    }),
    [profile?.displayName, profile?._id],
  );

  const methods = useForm<GeneralFormValues>({
    resolver: zodResolver(generalSchema),
    defaultValues,
    // Validate on submit only — the General tab is short enough
    // that surfacing errors as-you-type would feel pedantic.
    mode: 'onSubmit',
  });

  // Re-hydrate whenever the profile identity changes (e.g. after a
  // successful save flips `logo.upload` → `logo.url`).
  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const onSubmit = methods.handleSubmit(async values => {
    // Build a sectioned patch with ONLY the fields the user actually
    // touched. The form-level Zod resolver already trims `displayName`
    // and rejects empty; we still gate `logo` on whether a fresh
    // upload was attached because the legacy persisted `{ id, url }`
    // shape would round-trip wastefully if we resent it.
    const dirty = methods.formState.dirtyFields;
    const patch: { displayName?: string; logo?: ImageWithUpload } = {};
    if (dirty.displayName && values.displayName != null) {
      patch.displayName = values.displayName;
    }
    if (dirty.logo && values.logo != null) {
      patch.logo = values.logo;
    }
    if (!patch.displayName && !patch.logo) return;
    try {
      await update.mutateAsync(patch);
      onSaved?.(patch);
    } catch (err) {
      // The mutation cache global handler surfaces the toast; we
      // keep the form open and let the user retry.
      console.error('Failed to save general profile:', err);
    }
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="space-y-6" noValidate>
        <DisplayNameField name="displayName" disabled={disabled} financing={chip('soft')} />
        <LogoField name="logo" disabled={disabled} financing={chip('soft')} />

        {!hideSubmit ? (
          <div className="flex items-center justify-end">
            <Button type="submit" disabled={disabled || update.isPending}>
              {update.isPending ? (
                <Spinner />
              ) : (
                <span className="flex items-center gap-2">
                  <CheckIcon className="size-4" />
                  {t('buttons.save')}
                </span>
              )}
            </Button>
          </div>
        ) : null}
      </form>
    </FormProvider>
  );
}

function pickLogo(
  profile: Partial<OrganizationProfile> | undefined,
): ImageWithUpload | undefined {
  return profile?.logo;
}
